import type { ProgressiveSentenceNotesArgs } from './useProgressiveSentenceNotes.types';
import type { ProgressiveSentenceNotesData } from './useProgressiveSentenceNotesData.types';
import type { NormalizedSentenceNotesResponse } from '../../sentenceNotes.types';

import * as queryKeys from 'constants/QueryKeys';
import * as errorUtils from 'utils/errors';
import * as helpers from './useProgressiveSentenceNotesQueries.helpers';
import * as sentenceNotesConstants from '../../sentenceNotes.constants';
import * as constants from './useProgressiveSentenceNotes.constants';
import * as sentenceNotesApi from '../../sentenceNotes.api';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSentenceNotesReduxActions } from 'shared/features/sentenceNotes/hooks';

export const useProgressiveSentenceNotesQueries = ({
  args,
  localState,
  localActions
}: {
  args: Pick<
    ProgressiveSentenceNotesArgs,
    'documentId' | 'type' | 'sentenceNoteId' | 'onFetchComplete' | 'skipReduxStorage'
  >;
  localState: ProgressiveSentenceNotesData['localState'];
  localActions: ProgressiveSentenceNotesData['localActions'];
}) => {
  const sentenceNotesReduxActions = useSentenceNotesReduxActions();

  const fetchSystemGeneratedSentenceNotes = useQuery({
    enabled: false,
    queryKey: queryKeys.COMPLEX_QUERY_KEY_GETTERS[
      queryKeys.QUERY_KEYS.SYSTEM_GENERATED_SENTENCE_NOTES
    ][queryKeys.QUERY_SECTIONS.RIGHT_PANEL](args.documentId),
    queryFn: async () => {
      try {
        const data = await helpers.fetchSystemGeneratedNotes({
          args: {
            documentId: args.documentId,
            skipReduxStorage: args.skipReduxStorage
          },
          events: {
            onSystemGeneratedNotesFetch:
              sentenceNotesReduxActions.fetchSystemGeneratedSentenceNotes,
            onSetLocalSentenceNotes: localActions.setLocalSentenceNotes
          }
        });

        return data;
      } catch (error) {
        errorUtils.logReactQueryError(error as Error);
        throw error;
      }
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false
  });

  const fetchSentenceNoteById = useQuery({
    enabled: Boolean(!localState.hasFetchedSingleSentenceNote && args.sentenceNoteId),
    queryKey: queryKeys.COMPLEX_QUERY_KEY_GETTERS[queryKeys.QUERY_KEYS.SENTENCE_NOTES][
      queryKeys.QUERY_SECTIONS.RIGHT_PANEL
    ]({
      id: args.sentenceNoteId,
      ...(args.skipReduxStorage ? { skipReduxStorage: true } : {})
    }),
    queryFn: async () => {
      try {
        const response = await helpers.fetchSentenceNotes({
          params: { id: args.sentenceNoteId },
          args,
          events: {
            onSentenceNotesFetch: sentenceNotesReduxActions.getSentenceNotes,
            onSetLocalSentenceNotes: localActions.setLocalSentenceNotes
          }
        });
        localActions.setHasFetchedSingleSentenceNote(true);
        return response;
      } catch (error) {
        errorUtils.logReactQueryError(error as Error);
        throw error;
      }
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false
  });

  const fetchSentenceNotes = useInfiniteQuery<NormalizedSentenceNotesResponse, Error>({
    enabled:
      localState.hasFetchedSingleSentenceNote ||
      (!localState.hasFetchedSingleSentenceNote && !args.sentenceNoteId),
    queryKey: [
      queryKeys.COMPLEX_QUERY_KEY_GETTERS[queryKeys.QUERY_KEYS.SENTENCE_NOTES][
        queryKeys.QUERY_SECTIONS.RIGHT_PANEL
      ](helpers.getRequestParams({ args, prevPageParams: {} })),
      ...(args.skipReduxStorage ? [{ skipReduxStorage: true }] : [])
    ],
    initialPageParam: constants.INITIAL_PARAMS,
    queryFn: async ({ pageParam }) => {
      try {
        const params = helpers.getRequestParams({
          args,
          prevPageParams: pageParam as Parameters<typeof sentenceNotesApi.getSentenceNotes>[0]
        });

        const response = await helpers.fetchSentenceNotes({
          params,
          args,
          events: {
            onSentenceNotesFetch: sentenceNotesReduxActions.getSentenceNotes,
            onSetLocalSentenceNotes: localActions.setLocalSentenceNotes
          }
        });

        localActions.setIsInitialFetchOfList(false);
        localActions.setTotalCount(response.total);

        if (response.offset + constants.INITIAL_PARAMS.limit >= response.total) {
          if (args.type === sentenceNotesConstants.SENTENCE_NOTE_TYPE.OBLIGATIONS) {
            await fetchSystemGeneratedSentenceNotes.refetch();
          }

          args.onFetchComplete?.();
        }

        return response;
      } catch (error) {
        errorUtils.logReactQueryError(error as Error);
        throw error;
      }
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    getNextPageParam: helpers.getNextPageParam(constants.INITIAL_PARAMS.limit)
  });

  return {
    fetchSystemGeneratedSentenceNotes,
    fetchSentenceNotes,
    fetchSentenceNoteById
  };
};
